import { FC } from "react"
import {
  StepUpAuthentication,
  AuthenticationStepUpCache,
  AvailableAndActiveSimCardsDocument,
  AvailableAndActiveSimCardsQuery,
  SimCardProductFragment,
  useAuthenticationStepUpCache,
} from "@phonero/common-ux"
import { useAppQuery, useCurrentSubscriptionId } from "@phonero/common-ux"
import {
  ApiProductState,
  OfferingContractCategory,
} from "@phonero/common-graphql/types"
import { PuxButton, PuxIcon, PuxModal } from "@phonero/pux-react"
import { arrowMore } from "@phonero/pux-icons/icons"
import css from "../SubscriptionPage.module.scss"
import { OrderSimCardWizard } from "./AddSubscriptionSimCards/OrderSimCardWizard"

export const OrderNewSimCardsButton: FC = () => {
  const { subscriptionId } = useCurrentSubscriptionId()
  const { data } = useAppQuery(AvailableAndActiveSimCardsDocument, {
    variables: { id: subscriptionId! },
  })
  return <SimCardDialog data={data} />
}

export const SimCardDialog = ({
  data,
}: {
  data: AvailableAndActiveSimCardsQuery | undefined
}) => {
  const { t } = useTranslation()
  const { subscriptionId } = useCurrentSubscriptionId()

  const availableCards = data?.mobileSubscription?.simCards?.filter(
    (p) => {
      if("__typename" in p && p.__typename === "SimCardProduct") {
          const simCardProduct = p as SimCardProductFragment
          // only allowed to order Available sim cards that are not excluded by other active products. 
          // https://phoneroas.atlassian.net/browse/ICM-2804
          return simCardProduct.state === ApiProductState.Available && !simCardProduct.excludedBy?.some(
          (exclusion) =>        
            exclusion?.state === ApiProductState.Active
        )
      }
      return false
    }
  ) as SimCardProductFragment[]

  const [showOrderSimCardDialog, setShowOrderSimCardDialog] = useState(false)

  const enableSimWithoutPin =
    data?.mobileSubscription?.contractCategory === OfferingContractCategory.M2M

  const { __typename, ...savedAddress } =
    data?.mobileSubscription?.simCardAddress! ?? ""

  const {
    isCacheActive,
    cachedItem,
    clear: clearAuthenticationCache,
  } = useAuthenticationStepUpCache()

  useEffect(() => {
    if (!isCacheActive) return

    if (cachedItem?.context === "OrderNewSim") {
      clearAuthenticationCache()
      setShowOrderSimCardDialog(true)
    }
  }, [
    isCacheActive,
    cachedItem,
    setShowOrderSimCardDialog,
    clearAuthenticationCache,
  ])

  // Does not need a payload as all the data is already on this page
  const getNewSimCache = (): AuthenticationStepUpCache => ({
    context: "OrderNewSim",
  })

  if (
    !(
      data?.mobileSubscription?.simCards &&
      data.mobileSubscription.simCards.length
    )
  ) {
    return null
  }

  return (
    <>
      <PuxButton
        type="button"
        color="primary"
        shape="round"
        className={css.orderNewSimCardButton}
        onClick={() => setShowOrderSimCardDialog(true)}
      >
        {t("Subscription.OrderNewSimCard")}
        <PuxIcon icon={arrowMore} className={css.icon} size="small" />
      </PuxButton>

      {showOrderSimCardDialog && (
        <PuxModal
          isOpen={showOrderSimCardDialog}
          onDidDismiss={() => {
            setShowOrderSimCardDialog(false)
            clearAuthenticationCache()
          }}
        >
          <StepUpAuthentication
            title={t("Subscription.OrderNewSimCard")}
            stepUpCache={getNewSimCache}
          >
            <OrderSimCardWizard
              availableCards={availableCards}
              subscriptionId={subscriptionId ?? ""}
              enableSimWithoutPin={enableSimWithoutPin}
              address={savedAddress}
              onClose={() => setShowOrderSimCardDialog(false)}
              userName={data?.mobileSubscription?.userName ?? undefined}
              msisdn={data?.mobileSubscription?.msisdn ?? undefined}
            />
          </StepUpAuthentication>
        </PuxModal>
      )}
    </>
  )
}
